/* eslint-disable max-classes-per-file */
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { EMPTY, OperatorFunction, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { i18nFeature } from '../state/i18n/i18n.reducers';
import { ToastActions } from '../state/toast/toast.actions';
import { ToastMessageType } from '../state/types';
import { chainHandler, createChainHandler } from './chain-handler';

export interface ServiceException {
  className: string;
  exception: string;
  lineNumber: number;
  message: string;
  methodName: string;
}

export interface ServiceWarningException extends ServiceException {
  errorLevel: string;
}

export type ErrorMessageType = Pick<ToastMessageType, 'summary' | 'detail'>;

type BeforeMessageProcessorCallback = (message: string) => string;

type AfterMessageProcessorCallback = (message: ErrorMessageType) => ErrorMessageType;

type CatchErrorConfig = {
  store: Store;
  beforeErrorMessageProcessor?: BeforeMessageProcessorCallback;
  afterErrorMessageProcessor?: AfterMessageProcessorCallback;
};

type ErrorChainContext = {
  error: unknown;
  translations: Record<string, string>;
  beforeErrorMessageProcessor?: BeforeMessageProcessorCallback;
};

class IgnorableError extends Error {}

class ToastError extends Error {
  constructor(public toastMessage: ErrorMessageType) {
    super();
  }
}

class ToastWarning extends Error {
  constructor(public toastMessage: ErrorMessageType) {
    super();
  }
}

// ------
// Error handlers

const defaultErrorHandler = createChainHandler(
  ({ translations }: ErrorChainContext): ToastError =>
    new ToastError({ summary: translations['DEFAULT_ERROR_SUMMARY'], detail: translations['DEFAULT_ERROR_DETAIL'] }),
);

const handleUnauthorizedError = createChainHandler(({ error }: ErrorChainContext): IgnorableError | null => {
  if (error instanceof HttpErrorResponse && error.status === 401) {
    return new IgnorableError();
  }
  return null;
});

// Error handler for mapping HTTP error message codes to error messages - with warning level
const handleHttpErrorsWithErrorCodeAndWarningLevel = createChainHandler(
  // eslint-disable-next-line complexity
  ({ error, translations, beforeErrorMessageProcessor }: ErrorChainContext): ToastWarning | null => {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const serviceException = error.error as ServiceWarningException;

    if (serviceException?.errorLevel !== 'WARNING') {
      return null;
    }

    const detail = translations[beforeErrorMessageProcessor(serviceException?.message)];
    if (detail) {
      return new ToastWarning({ summary: translations['DEFAULT_ERROR_SUMMARY'], detail });
    }
    return null;
  },
);

// Error handler for mapping HTTP error message codes to error messages
const handleHttpErrorsWithErrorCode = createChainHandler(
  ({ error, translations, beforeErrorMessageProcessor }: ErrorChainContext): ToastError | null => {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const serviceException = error.error as ServiceException;
    const detail = translations[beforeErrorMessageProcessor(serviceException?.message)];

    if (detail) {
      return new ToastError({ summary: translations['DEFAULT_ERROR_SUMMARY'], detail });
    }
    return null;
  },
);

// Error handler for mapping HTTP error messages to error messages
const handleHttpErrorsWithErrorMessage = createChainHandler(
  ({ error, translations }: ErrorChainContext): ToastError | null => {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const serviceException = error.error as ServiceException;
    if (!serviceException?.message) {
      return null;
    }
    return new ToastError({
      summary: `${translations['DEFAULT_ERROR_SUMMARY']} - ${serviceException.message}`,
      detail: translations['DEFAULT_ERROR_DETAIL'],
    });
  },
);

// Used by document service to handle errors when saving files.
// Not fully sure if this is needed anymore, but leaving it here for now. It might be useful for other services as well.
// eslint-disable-next-line complexity
const handleErrorWithJsonBody = createChainHandler(({ error, translations }: ErrorChainContext): ToastError | null => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!(error as any)?._body) {
    return null;
  }

  try {
    const body = JSON.parse((error as any)._body);
    return new ToastError({
      summary: `${translations['DEFAULT_ERROR_SUMMARY']} - ${body?.message ?? ''}`,
      detail: translations['DEFAULT_ERROR_DETAIL'],
    });
  } catch {
    return null;
  }
});

// Error handlers sorted by priority
const errorHandlers = [
  handleUnauthorizedError,
  handleHttpErrorsWithErrorCodeAndWarningLevel,
  handleHttpErrorsWithErrorCode,
  handleHttpErrorsWithErrorMessage,
  handleErrorWithJsonBody,
  defaultErrorHandler,
];
const errorChainHandler = chainHandler(errorHandlers);

const createErrorTranslationObservable = (store: Store) =>
  store.select(i18nFeature.selectErrors).pipe(filter(Boolean), take(1));

export const catchErrorAndShowMessage = ({
  store,
  beforeErrorMessageProcessor = (message) => message,
  afterErrorMessageProcessor = (message) => message,
}: CatchErrorConfig): OperatorFunction<any, any> =>
  catchError((error: unknown) => {
    return createErrorTranslationObservable(store).pipe(
      switchMap((translations) => {
        const context = { error, translations, beforeErrorMessageProcessor };
        const result: Error | null = errorChainHandler.handle(context);

        if (result instanceof IgnorableError) {
          return EMPTY;
        }

        if (result instanceof ToastError) {
          store.dispatch(ToastActions.showErrorMessage(afterErrorMessageProcessor(result.toastMessage)));
        }

        if (result instanceof ToastWarning) {
          store.dispatch(ToastActions.showWarnMessage(afterErrorMessageProcessor(result.toastMessage)));
        }

        return throwError(() => error);
      }),
    );
  });
